import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { apiClient } from "@/api/apiClient";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const LoginPage = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const [restoreDialog, setRestoreDialog] = useState({ open: false, deletionDate: null });
    const [showResendDialog, setShowResendDialog] = useState(false);
    const [resendEmail, setResendEmail] = useState('');
    const [isResending, setIsResending] = useState(false);

    const handleResend = async () => {
        if (!resendEmail) {
            toast.error("Veuillez entrer votre email");
            return;
        }
        setIsResending(true);
        try {
            await apiClient.auth.resendVerification({ email: resendEmail });
            toast.success("Email envoyé ! Vérifiez votre boîte de réception.");
            setShowResendDialog(false);
        } catch (error) {
            toast.error(error.response?.data?.error || "Erreur lors de l'envoi");
        } finally {
            setIsResending(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            await apiClient.auth.login(formData);
            
            // Invalidate the user query to ensure the app fetches the new authenticated state
            queryClient.invalidateQueries({ queryKey: ['user'] });
            
            toast.success("Connexion réussie");
            navigate('/swipe');
        } catch (error) {
            // console.error(error);
            // Check for scheduled deletion error
            const errorCode = error.response?.data?.code;

            // Check for scheduled deletion error
            if (errorCode === 'ACCOUNT_SCHEDULED_FOR_DELETION') {
                setRestoreDialog({
                    open: true,
                    deletionDate: error.response.data.scheduled_deletion_at
                });
            } else if (errorCode === 'EMAIL_NOT_VERIFIED') {
                toast.error("Email non vérifié", {
                    description: "Veuillez vérifier votre email avant de vous connecter. Vérifiez vos spams si besoin."
                });
            } else {
                toast.error(error.response?.data?.error || "Erreur de connexion");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleRestore = async () => {
        setIsLoading(true);
        try {
            // Call cancel deletion endpoint (using same credentials from form)
            await apiClient.auth.cancelDeletion(formData);
            toast.success("Suppression annulée ! Bon retour parmi nous.");
            setRestoreDialog({ open: false, deletionDate: null });
            navigate('/swipe');
        } catch (error) {
            toast.error("Échec de la restauration du compte");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-[hsl(228,14%,6%)] relative">
            <Button 
                variant="ghost" 
                className="absolute top-4 left-4 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                onClick={() => navigate('/home')}
            >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour au menu
            </Button>
            <Card className="w-[350px]">
                <CardHeader>
                    <CardTitle>Connexion</CardTitle>
                    <CardDescription>Connectez-vous pour accéder à votre compte</CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent>
                        <div className="grid w-full items-center gap-4">
                            <div className="flex flex-col space-y-1.5">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="nom@exemple.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="flex flex-col space-y-1.5">
                                <div className="flex justify-between items-center mb-1.5">
                                    <Label htmlFor="password">Mot de passe</Label>
                                    <Link to="/forgot-password" className="text-xs text-amber-600 dark:text-amber-400 hover:underline">
                                        Mot de passe oublié ?
                                    </Link>
                                </div>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col gap-4">
                        <Button className="w-full" type="submit" disabled={isLoading}>
                            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Se connecter
                        </Button>
                        <div className="text-sm text-center text-gray-500 flex flex-col gap-2">
                            <div>Pas encore de compte ? <Link to="/register" className="text-amber-600 dark:text-amber-400 hover:underline">S'inscrire</Link></div>
                            <Button variant="link" className="text-xs h-auto p-0 text-gray-400" onClick={() => setShowResendDialog(true)}>
                                Email de vérification non reçu ?
                            </Button>
                        </div>
                    </CardFooter>
                </form>
            </Card>

            {/* Resend Verification Dialog */}
            <Dialog open={showResendDialog} onOpenChange={setShowResendDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Renvoyer l'email de vérification</DialogTitle>
                        <DialogDescription>
                            Entrez votre adresse email pour recevoir un nouveau lien.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Label htmlFor="resend-email">Email</Label>
                        <Input
                            id="resend-email"
                            placeholder="nom@exemple.com"
                            value={resendEmail}
                            onChange={(e) => setResendEmail(e.target.value)}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowResendDialog(false)}>Annuler</Button>
                        <Button onClick={handleResend} disabled={isResending}>
                            {isResending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Renvoyer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Restore Account Dialog */}
            <Dialog open={restoreDialog.open} onOpenChange={(open) => !open && setRestoreDialog({ ...restoreDialog, open: false })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="text-amber-600 dark:text-amber-500">Compte en cours de suppression</DialogTitle>
                        <DialogDescription>
                            Ce compte est programmé pour être supprimé le <strong>{restoreDialog.deletionDate && new Date(restoreDialog.deletionDate).toLocaleDateString()}</strong>.
                            <br /><br />
                            Voulez-vous annuler la suppression et récupérer votre compte ?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRestoreDialog({ ...restoreDialog, open: false })}>Non, quitter</Button>
                        <Button
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={handleRestore}
                            disabled={isLoading}
                        >
                            {isLoading ? "Restauration..." : "Oui, récupérer mon compte"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default LoginPage;
